"use client";

import { propertiesPlugin } from "@analytics";
import { usePathname, useSearchParams } from "next/navigation";
import {
  type PropsWithChildren,
  Suspense,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface RouteContextType {
  previousRoute: string | null;
}

const RouteContext = createContext<RouteContextType | undefined>(undefined);

function RouteProviderInner({ children }: PropsWithChildren) {
  const [previousRoute, setPreviousRoute] = useState<string | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Construct full path with search params
    const search = searchParams?.toString();
    const fullPath = search ? `${pathname}?${search}` : pathname;

    // Only update if path actually changed
    if (
      currentPathRef.current !== null &&
      fullPath !== currentPathRef.current
    ) {
      setPreviousRoute(currentPathRef.current);
      propertiesPlugin.setReferrer(currentPathRef.current);
    }

    currentPathRef.current = fullPath;
  }, [pathname, searchParams]);

  return (
    <RouteContext.Provider value={{ previousRoute }}>
      {children}
    </RouteContext.Provider>
  );
}

export const RouteProvider = ({ children }: PropsWithChildren) => {
  return (
    <Suspense fallback={null}>
      <RouteProviderInner>{children}</RouteProviderInner>
    </Suspense>
  );
};

export const usePreviousRoute = (): string | null => {
  const context = useContext(RouteContext);
  if (!context) {
    throw new Error("usePreviousRoute must be used within a RouteProvider");
  }
  return context.previousRoute;
};
